import { ROUTE, type Route } from "./paths";

/** Каноничные роли (без null) */
export type RoleName = "owner" | "superadmin" | "admin" | "door" | "guest";
export type Role = RoleName | null;

/** Нормализация роли из БД/серверов. Поддерживаем алиасы. */
export function normalizeRole(r?: string | null): RoleName | null {
  const v = String(r ?? "").trim().toLowerCase();
  if (v === "staff") return "door"; // алиас прежнего нейминга
  if (v === "owner" || v === "superadmin" || v === "admin" || v === "door" || v === "guest") return v;
  return null;
}

/** Возможности (capabilities) */
export type Capability =
  | "scan_qr"
  | "view_stats"
  | "manage_events"
  | "manage_prices"
  | "manage_finance"
  | "manage_settings"
  | "messages"
  | "manage_staff"
  | "dangerous_ops";

/** Политика: capability → кто может */
export const POLICY: Record<Capability, RoleName[]> = {
  scan_qr:        ["door", "admin", "superadmin", "owner"],
  view_stats:     ["admin", "superadmin", "owner"],
  manage_events:  ["admin", "superadmin", "owner"],
  manage_prices:  ["admin", "superadmin", "owner"],
  messages:       ["admin", "superadmin", "owner"],
  manage_finance: ["superadmin", "owner"],
  manage_settings:["admin", "superadmin", "owner"],
  manage_staff:   ["superadmin", "owner"],
  dangerous_ops:  ["owner"],
};

/** Проверка возможности */
export function can(role: Role, cap: Capability): boolean {
  const r = normalizeRole(role);
  if (!r) return false;
  return (POLICY[cap] as readonly string[]).includes(r);
}

/** Домашний маршрут для роли */
export function homeRouteFor(role: Role): Route {
  const r = normalizeRole(role);
  if (r === "owner" || r === "superadmin" || r === "admin") return ROUTE.super();
  if (r === "door") return ROUTE.door();
  return ROUTE.guest();
}

/** Разрешение на конкретный маршрут */
export function isRouteAllowed(route: Route, role: Role): boolean {
  // /door/*
  if (ROUTE.isDoorHome(route) || ROUTE.isDoorScan(route) || ROUTE.isDoorToken(route)) {
    return can(role, "scan_qr");
  }

  // /super (главная панель)
  if (ROUTE.isSuperHome(route)) {
    const r = normalizeRole(role);
    return r === "owner" || r === "superadmin" || r === "admin";
  }

  // /super/events*
  if (
    ROUTE.isSuperEventsHome(route) ||
    ROUTE.isSuperEventNew(route) ||
    ROUTE.isSuperEventEdit(route)
  ) {
    return can(role, "manage_events");
  }

  // /super/staff*
  if (ROUTE.isSuperStaffHome(route) || ROUTE.isSuperStaffRoles(route)) {
    return can(role, "manage_staff");
  }

  // /super/settings (owner only)
  if (ROUTE.isSuperSettings(route)) {
    return can(role, "manage_settings");
  }

  // /guest и корень — доступны всем
  if (ROUTE.isGuestHome(route) || route.name === "root") return true;

  // всё остальное — закрыто
  return false;
}
